import { NAV_ITEMS, OTHER_NAV_ITEMS } from '@/../shared-constants/constants';
import { defaultGenericFieldsAndLabelsToRender } from './labelsToRender';
import {
  CustomerConfigTranslatedStringKeyPaths,
  TranslationTypes,
} from './translations/constants';
import { t } from './translations/helpers';
import { DashboardAvailableDataset, PropertyOverviewTabs } from './types';

export { NAV_ITEMS, OTHER_NAV_ITEMS };

export const CITY_MODES = {
  oversight: 'oversight',
  trial: 'trial',
  customer: 'customer',
};

export const DISCLAIMER_FOR_CITY_MODES = {
  [CITY_MODES.customer]: null,
  [CITY_MODES.oversight]:
    'Disclaimer: The map results display only listing data. The accuracy of the map marker is subject to the original marker placement by the party publishing the listing. To obtain precise location and identified properties, please ',
  [CITY_MODES.trial]:
    'Disclaimer: The map results display a limited number of identified properties and listing data. The accuracy of the map marker is subject to the original marker placement by the party publishing the listing. To obtain precise location and identified properties, please ',
};

export const DEFAULT_CITY_MODE = CITY_MODES.customer;

// types of statistical data we can display in the dashboard
export const datasetTypes: Record<
  DashboardAvailableDataset,
  DashboardAvailableDataset
> = {
  CURRENT_STR_PROPERTIES: 'CURRENT_STR_PROPERTIES',
  OCCUPANCY: 'OCCUPANCY',
  AVG_DAILY_RATE: 'AVG_DAILY_RATE',
  REVENUE: 'REVENUE',
  BOOKED: 'BOOKED',
  TRANSIENT_OCCUPANCY_TAX: 'TRANSIENT_OCCUPANCY_TAX',
  REGISTRATION_RATE: 'REGISTRATION_RATE',
  UNIT_TYPE: 'UNIT_TYPE', // stats about types of rental properties
  RESIDENTIAL_TYPE: 'RESIDENTIAL_TYPE', // stats about types of residential properties
  ONLINE_PLATFORMS: 'ONLINE_PLATFORMS', // online platforms trends over time
  RENTAL_SIZE: 'RENTAL_SIZE',
  RENTAL_STRUCTURE: 'RENTAL_STRUCTURE',
  MOST_ACTIVE_HOSTS: 'MOST_ACTIVE_HOSTS',
  MOST_ACTIVE_OWNERS: 'MOST_ACTIVE_OWNERS',
  TOP_ONLINE_PLATFORMS: 'TOP_ONLINE_PLATFORMS', // slected online platforms that we scape
  RENTAL_CATEGORIES: 'RENTAL_CATEGORIES',
  STACKED_BOOKED_RATIO: 'STACKED_BOOKED_RATIO',
  ESTIMATED_RENTAL_ACTIVITIES: 'ESTIMATED_RENTAL_ACTIVITIES',
};

/**
 * The data Schema specifies that camelCasing should be used, this is used to relate
 * datasetTypes to the appropriate data keys
 */
export const dataTypeKeyNameMap = {
  CURRENT_STR_PROPERTIES: 'currentStrProperties',
  OCCUPANCY: 'occupancy',
  // BOOKED: 'booked',
  REVENUE: 'revenue',
  AVG_DAILY_RATE: 'adr',
  TRANSIENT_OCCUPANCY_TAX: 'estimateTot',
  // REGISTRATION_RATE: 'registrationRate',
  UNIT_TYPE: 'unitType',
  RESIDENTIAL_TYPE: 'residentialType',
  ONLINE_PLATFORMS: 'onlinePlatforms',
  RENTAL_SIZE: 'rentalSize',
  RENTAL_STRUCTURE: 'rentalStructure',
  MOST_ACTIVE_HOSTS: 'mostActiveHosts',
  MOST_ACTIVE_OWNERS: 'mostActiveOwners',
  TOP_ONLINE_PLATFORMS: 'onlinePlatforms',
  RENTAL_CATEGORIES: 'rentalCategories',
};

export const datasetTypeLabels = {
  [datasetTypes.CURRENT_STR_PROPERTIES]: 'STR Properties',
  [datasetTypes.OCCUPANCY]: 'Booking Activities',
  [datasetTypes.AVG_DAILY_RATE]: 'Average Daily Rate',
  [datasetTypes.REVENUE]: 'Host Revenue',
  [datasetTypes.TRANSIENT_OCCUPANCY_TAX]: `${t(
    CustomerConfigTranslatedStringKeyPaths.TOT,
    TranslationTypes.LONG
  )}`,
  [datasetTypes.REGISTRATION_RATE]: 'Avg. Registration Rate',
  [datasetTypes.UNIT_TYPE]: 'Unit Type',
  [datasetTypes.RESIDENTIAL_TYPE]: 'Residential Type',
  [datasetTypes.ONLINE_PLATFORMS]: 'Online Platforms',
  [datasetTypes.ESTIMATED_RENTAL_ACTIVITIES]: 'Estimated Rental Activities',
  [datasetTypes.RENTAL_SIZE]: 'Rental Size',
  [datasetTypes.RENTAL_STRUCTURE]: 'Rental Structure',
  [datasetTypes.MOST_ACTIVE_HOSTS]: 'Most Active Hosts',
  [datasetTypes.MOST_ACTIVE_OWNERS]: 'Most Active Owners',
  [datasetTypes.TOP_ONLINE_PLATFORMS]: 'Top Online Platforms',
  [datasetTypes.RENTAL_CATEGORIES]: 'Rental Categories',
  [datasetTypes.BOOKED_RATIO]: 'Booked vs. Blocked Ratio',
};

export const dataTypeDescription = {
  [datasetTypes.CURRENT_STR_PROPERTIES]: 'STR Properties This Month',
  [datasetTypes.OCCUPANCY]: 'Occupancy This Month',
  [datasetTypes.AVG_DAILY_RATE]: 'Avg Rate This Month',
  [datasetTypes.REVENUE]: 'Total Revenue',
  [datasetTypes.BOOKED]: 'Nights Booked in past 12 Months',
  [datasetTypes.TRANSIENT_OCCUPANCY_TAX]: `Total ${t(
    CustomerConfigTranslatedStringKeyPaths.TOT,
    TranslationTypes.SHORT
  )}`,
};

export const datasetTypeSymbols = {
  [datasetTypes.CURRENT_STR_PROPERTIES]: 'listings',
  [datasetTypes.OCCUPANCY]: '%',
  [datasetTypes.AVG_DAILY_RATE]: '$',
  [datasetTypes.REVENUE]: '$',
  [datasetTypes.TRANSIENT_OCCUPANCY_TAX]: '$',
  [datasetTypes.REGISTRATION_RATE]: '%',
};

export const chartThemes = {
  DASHBOARD: 'DASHBOARD',
  DASHBOARD_PRINT: 'DASHBOARD_PRINT',
  PROPERTY_DETAILS: 'PROPERTY_DETAILS',
  MODAL: 'MODAL',
};

export const themedChartHeights = {
  [chartThemes.DASHBOARD]: '100%',
  [chartThemes.DASHBOARD_PRINT]: 350,
  [chartThemes.PROPERTY_DETAILS]: 120,
  [chartThemes.MODAL]: 446,
};

// Required by sidebar panel

export const datasetSubtypeLabelsProper = {
  unitType: {
    selfContained: 'Full Unit',
    room: 'Partial',
    unknown: 'Unknown',
  },
  rentalStructure: {
    mainStructure: 'Main Structure',
    adu: 'Additional Dwelling Unit',
    multiFamily: 'Multifamily',
    unknown: 'Unknown',
  },
  ownerOccupied: {
    true: 'Yes',
    false: 'No',
  },
};

export const subtypeChartLabels = {
  unitType: {
    unknown: 'Unknown',
    selfContained: 'Full Unit',
    room: 'Partial',
  },
  rentalType: {
    unknown: 'Unknown',
    selfContained: 'Self Contained',
    room: 'Room',
    other: 'Other',
  },
  propertyType: {
    current: 'Current',
    other: 'Other',
  },
  residentialType: {
    primary: 'Primary',
    secondaryInvestment: 'Secondary/Investment',
    unknown: 'Unknown',
  },
  onlinePlatforms: {
    airbnb: 'Airbnb',
    vrbo: 'Vrbo',
    homeaway: 'Homeaway',
    flipkey: 'Flipkey',
    booking: 'Booking',
    other: 'Others',
  },
  rentalStructure: {
    mainStructure: 'Main Structure',
    adu: 'ADU',
    multiFamily: 'Multifamily',
    other: 'Other',
    unknown: 'Unknown',
  },
  rentalSize: {
    '1br': 'STU/1R',
    '2br': '2R',
    '3br': '3R',
    '4br+': '4R',
  },
  bookedRatio: {
    booked: 'BOOK %',
    blocked: 'BLOCK %',
  },
  transientOccupancyTax: {
    actual: 'Actual',
    estimated: 'Estimated',
  },
  ownerOccupied: {
    true: 'Yes',
    false: 'No',
  },
};

export const VERIFIED_FLAG_LABELS = {
  true: 'Yes',
  false: 'No',
};

export const onlinePlatforms = {
  AIRBNB: 'airbnb',
  VRBO: 'vrbo',
  BOOKING: 'booking',
  HOME_AWAY: 'homeaway',
  TRIP_ADVISOR: 'tripAdvisor',
  FLIP_KEY: 'flipkey',
  OTHERS: 'others',
};

export const VERIFICATION_STATUES = {
  VERIFIED: 'verified',
  PENDING_VERIFICATION: 'pending',
  UNVERIFIED: 'unverified',
};

const ROOT_NAV_ITEM_KEY = undefined;

// names of url sub-paths use for navigation.. e.g.: localhost:1234/<url nav>/<deckardid>
export const URL_NAVS = {
  ...NAV_ITEMS,
  [ROOT_NAV_ITEM_KEY]: NAV_ITEMS.dashboard, // this represents the '/' route
  profile: 'profile',
  CONTACT: 'contact',
  legal: 'legal',
  complaints: NAV_ITEMS.complaints,
};

// -------------map filter checkbox values----------------------------
// TODO: Remove this object. keys and incoming values in data should match

export const PROPERTY_DATA_FILTER_KEYS = {
  complaints: 'complaints',
  violations: 'violations',
  onlinePlatforms: 'onlinePlatforms',
  hoe: 'hoe',
};

export const PLATFORM_FILTER_OPT_NONE = 'none';

export const PROPERTY_DATA_KV_MAP = {
  // values are what I get from api
  residentialType: {
    primary: 'primary',
    secondaryInvestment: 'secondaryInvestment',
    unknown: 'unknown',
  },
  visibility: {
    liveNow: 'liveNow',
    liveRecently: 'LiveRecently',
    notLive: 'notLive',
  },
  addressIdentification: {
    identifiedListing: 'identifiedListing',
    unidentifiedListing: 'unidentifiedListing',
  },
  strLicense: {
    hasStrLicense: 'hasStrLicense',
    hasNoStrLicense: 'hasNoStrLicense',
  },
  locationType: {
    within: 'within',
    selfReported: 'self-reported',
    neighboring: 'neighboring',
  },
  unitType: {
    selfContained: 'selfContained',
    room: 'room',
    unknown: 'unknown',
    other: 'other',
  },
  rentalStructure: {
    mainStructure: 'mainStructure',
    adu: 'adu',
    multiFamily: 'multiFamily',
    unknown: 'unknown',
  },
  onlinePlatforms: {},
  activity: {
    hasBookings: 'hasBookings',
    hasAvailability: 'hasAvailability',
    isBlocked: 'isBlocked',
  },
  complaints: {
    hasComplaints: 'hasComplaints',
    hasNoComplaints: 'hasNoComplaints',
    complaintTypes: 'complaintTypes',
  },
  violations: {
    hasViolations: 'hasViolations',
    hasNoViolations: 'hasNoViolations',
    violationTypes: 'violationTypes',
  },
  verification: {
    isVerified: 'isVerified',
    isUnverified: 'isUnverified',
  },
  verifiedStatus: {
    isVerified: VERIFICATION_STATUES.VERIFIED,
    isPending: VERIFICATION_STATUES.PENDING_VERIFICATION,
    isUnverified: VERIFICATION_STATUES.UNVERIFIED,
  },
  hoe: {
    claimingHoe: 'claimingHoe',
    notClaimingHoe: 'notClaimingHoe',
  },
};

export const propertyActivityValues = {
  hasBookings: 'Has Bookings',
  hasAvailability: 'Has Availability',
  isBlocked: 'Blocked',
};

export const propertyVisibilityValues = {
  liveNow: 'Live Now',
  notLive: 'Not Live',
  liveRecently: 'Live Recently',
};
export const dashCardListModalTexts = {
  KPI_CARD: {
    title: 'KPI',
    body: 'Time series displaying key metrics active listings, occupancy and revenue on a monthly basis with the latest monthly figures are displayed on the headline tiles. Each of the data points on the line graph display the three KPI values on that month.',
  },
  MOST_ACTIVE_HOSTS: {
    title: 'Most Active Hosts',
    body: 'The list shows the highest-ranking hosts based on aggregate revenue.',
  },
  MOST_ACTIVE_OWNERS: {
    title: 'Most Active Owners',
    body: 'The list shows the highest-ranking owners based on aggregate revenue.',
  },
  CURRENT_STR_PROPERTIES: {
    title: 'STR Properties',
    body: 'Chart showing the total number of monthly STR properties over the past year.',
  },
  OCCUPANCY: {
    title: 'Latest Occupancy',
    body: 'Chart showing the estimated total of all monthly bookings over the last six months.',
  },
  REVENUE: {
    title: 'Host Revenue',
    body: 'Chart showing the estimated product of all monthly bookings multiplied by monthly Average Daily Rate (ADR) over the last six months.',
  },
  AVG_DAILY_RATE: {
    title: 'ADR',
    body: 'Monthly Average Daily Rate (ADR) of all STR properties over a six month period.',
  },
  TRANSIENT_OCCUPANCY_TAX: {
    title: 'Transient Occupancy Tax - Estimated / Actual',
    body: 'Monthly difference between total estimated revenue from unregistered STRS and the set Transient Occupancy Tax (TOT) percentage.  City data required to display this chart.',
  },
  REGISTRATION_RATE: {
    title: 'Avg. Registration Rate',
    body: 'Monthly rate of city filings for STR registrations. City data required to display this chart.',
  },
  RESIDENTIAL_TYPE: {
    title: 'Residential Type',
    body: 'Category breakdown of STRs by residential tax class.',
  },
  RENTAL_STRUCTURE: {
    title: 'Rental Structure',
    body: 'Monthly category breakdown of STR properties by structure designations over six months.',
  },
  UNIT_TYPE: {
    title: 'Unit Type',
    body: 'Category breakdown of STRs by structure.',
  },
  ONLINE_PLATFORMS: {
    title: 'Online Platforms',
    body: 'Monthly distribution of listings on online platforms.',
  },
  TOP_ONLINE_PLATFORMS: {
    title: 'Top Online Platforms',
    body: 'Monthly distribution of listings on online platforms.',
  },
  RENTAL_SIZE: {
    title: 'Rental Size',
    body: 'Monthly distribution of room count of all the identified STRs over six months.',
  },
  RENTAL_CATEGORIES: {
    title: 'Rental Size',
    body: 'Distribution of room count of all the identified STRs.',
  },
};

export const contentViewOptions = {
  ACTIVE_OWNERS: 'ACTIVE_OWNERS',
  ACTIVE_HOSTS: 'ACTIVE_HOSTS',
  ALL_PROPERTIES: 'ALL_PROPERTIES',
};

// details grid view modals
export const platformAssetIdPrefixes = {
  property: 'deckardid',
  host: 'host',
  owner: 'owner',
};

export const platformAssetPKField = {
  property: 'deckardid',
  host: 'host',
  owner: 'owner',
};

export const fallbackTexts = {
  UNIDENTIFIED: 'Unidentified',
  ADDRESS_IDENTIFICATION_IN_PROGRESS: 'Address Identification In Progress',
  PENDING_IDENTIFICATION: 'Pending Identification',
  UNKNOWN: 'Unknown',
  IDENTIFICATION_IN_PROGRESS: 'Identification In-Process',
  NOT_FOUND: 'Not Found',
  PENDING: 'Pending',
};

export const RENTALSCAPE_PROD = 'https://rentalscape.deckard.technology';
export const RENTALSCAPE_STG = 'https://rentalscape.staging.deckard.technology';
export const RENTALSCAPE_DEV = 'https://rentalscape.dev.deckard.technology';

export const DUMMY_DATA_USER_GROUP = 'hardcoded-data-delmar';

export const OFFLINE_MODE_DATA_GROUP = 'offline-mode-data';

export const IMPERIAL_BEACH_SAMPLE = 'imperialbeachdummy';

// S3 Bucket constants
export const DATA_BUCKETS = {
  GEO_ASSETS: {
    region: 'us-east-2',
    bucket: 'deckard-geo-assets-us-east-2',
  },
  MOCK_DATA: {
    region: 'us-west-2',
    bucket: 'rentalscape-demo-data',
    kpi_path: 'usa-ca-sandiego-delmar/kpi-data.json',
    propertyList_path: 'usa-ca-sandiego-delmar/property-list.json',
  },
  IMPERIAL_BEACH_SAMPLE_DATA: {
    region: 'us-west-2',
    bucket: 'rentalscape-demo-data',
    kpi_path: 'usa-ca-chula-vista/data/kpi_imperial_beach_natasha.json',
    hosts_path: 'usa-ca-chula-vista/data/imperial_beach_hosts.json',
    owners_path: 'usa-ca-chula-vista/data/imperial_beach_owners.json',
    zoning1_path: 'usa-ca-chula-vista/data/ib_strzones1.geojson',
    zoning2_path: 'usa-ca-chula-vista/data/ib_strzones2.geojson',
    zoning3_path: 'usa-ca-chula-vista/data/ib_strzones3.geojson',
    propertyList_path:
      'usa-ca-chula-vista/data/propertyList_imperial_beach_natasha.json',
    shapefile: 'usa-ca-chula-vista/shapefile/imperialBeach.geojson',
  },
};

export const offlineDevUser = {
  offlineDev: true,
  username: 'offline',
  signInUserSession: { isValid: () => true },
};

export const offlineDevFakeSessionObj = {
  idToken: { payload: { 'cognito:groups': [OFFLINE_MODE_DATA_GROUP] } },
};

// TODO: This needs to get fetched from the server
export const groupToCity = {
  'usa-ca-sandiego-sandiego': 'San Diego, CA',
  'usa-ca-sandiego-delmar': 'Del Mar, CA',
};

export const BASE_URL_INDEX = 1;

export const FIRST_PAGE = 1;

export const FIRST_ITEM_INDEX = 0;

export const SECOND_ITEM_INDEX = 1;

export const EMPTY_LIST = 0;

export const COMMA_LIST_SEPARATOR = ', ';

export const CREATE_CITATION_NO_PROPERTIES_SELECTED =
  'Select at least one property on the list to enable citation generation';

export const ADDED_NOTE = 'added a note';

export const RSUITE_ALERT_DURATION = {
  // time in ms
  NONE: 0,
  DEFAULT: 5000,
  MEDIUM: 8000,
  LONG: 10000,
};

export const DETAILS_TABS = {
  PROPERTY: 'property',
  ACTIVITY: 'activity',
  LICENSES: 'licenses',
};

// Calendar Data JSON Keys
export const calendarDataStatusOptions = {
  // Looks into the future, rental is neither booked nor blocked (can be booked)
  BOOKABLE: 'bookable',
  // Looks to the past, rental is either blocked or not booked,
  //  but can't be booked due to minimum stay requirements, etc
  UNBOOKABLE: 'unbookable',
  BOOKED: 'booked', // ML engine believes this is booked
  BLOCKED: 'blocked', // ML engine believes this is blocked
  UNKNOWN: 'unknown', // Not enough data to make the determination
};

export const PROPERTY_DETAILS_TAB_NAMES: Record<string, PropertyOverviewTabs> =
  {
    overview: 'overview',
    license: 'license',
    activity: 'activity',
    complaints: 'complaints',
    history: 'history',
    inspections: 'inspections',
    propertyOverviewTaxTab: 'propertyOverviewTaxTab',
    // value should match with name in enabledBetaFeatures in customer config. It is checked in dashboard-selectors
    violations: 'violationsTab',
  };

export const DEFAULT_PROPERTY_DETAILS_TAB = PROPERTY_DETAILS_TAB_NAMES.overview;

export const ALWAYS_ENABLED_PROPERTY_DETAILS_TABS = new Set([
  DEFAULT_PROPERTY_DETAILS_TAB,
  PROPERTY_DETAILS_TAB_NAMES.history,
]);

export const licenseManagementDefaultConfig = {
  secondaryDisplay: [],
  licenseInfoRenderConfig: defaultGenericFieldsAndLabelsToRender,
};

export const LICENSE_GROUP = 'license';
export const LICENSE_APP_GROUP = 'licenseApplication';
export const WAITLIST_GROUP = 'waitlist';

export const DEFAULT_LICENSE_STATUS_MAPPING = {
  closed: 'closed',
  created: 'created',
  current: 'current',
  denied: 'denied',
  expired: 'expired',
  returned: 'returned',
  reviewed: 'reviewed',
  revoked: 'revoked',
  submitted: 'submitted',
  suspended: 'suspended',
  withdrawn: 'withdrawn',
  waitlist: 'waitlist',
  invited: 'invited',
};

export const LICENSE_STATUS_RENEWAL_SUBMITTED = 'renewal_submitted';
export const DEFAULT_RENEWAL_SUBMITTED_FORMATTED_VALUE = 'Renewal Submitted';

/**
 * License module control button/dropdown items
 * Use an array to store the group(s) each status belongs to
 * in order to have the 'current' status to be displayed in all
 * dropdown list options
 */
export const licenseStatusInfo = {
  // Pending Status Group
  [DEFAULT_LICENSE_STATUS_MAPPING.waitlist]: {
    group: [LICENSE_APP_GROUP, WAITLIST_GROUP],
  },
  [DEFAULT_LICENSE_STATUS_MAPPING.invited]: {
    group: [LICENSE_APP_GROUP, WAITLIST_GROUP],
  },
  [DEFAULT_LICENSE_STATUS_MAPPING.created]: { group: [LICENSE_APP_GROUP] },
  [DEFAULT_LICENSE_STATUS_MAPPING.submitted]: { group: [LICENSE_APP_GROUP] },
  [DEFAULT_LICENSE_STATUS_MAPPING.returned]: { group: [LICENSE_APP_GROUP] },
  [DEFAULT_LICENSE_STATUS_MAPPING.reviewed]: { group: [LICENSE_APP_GROUP] },
  // Issued Status Group
  [DEFAULT_LICENSE_STATUS_MAPPING.current]: {
    group: [LICENSE_GROUP, LICENSE_APP_GROUP],
  },
  // Restricted Status Group
  [DEFAULT_LICENSE_STATUS_MAPPING.suspended]: { group: [LICENSE_GROUP] },
  // Archived Status Group
  [DEFAULT_LICENSE_STATUS_MAPPING.closed]: { group: [LICENSE_APP_GROUP] },
  [DEFAULT_LICENSE_STATUS_MAPPING.denied]: { group: [LICENSE_APP_GROUP] },
  [DEFAULT_LICENSE_STATUS_MAPPING.expired]: { group: [LICENSE_GROUP] },
  [DEFAULT_LICENSE_STATUS_MAPPING.withdrawn]: { group: [LICENSE_GROUP] },
  [DEFAULT_LICENSE_STATUS_MAPPING.revoked]: { group: [LICENSE_GROUP] },
  /**
   * TODO: This should be store stored in stats in some way
   * Placer has it's own set of statuses that don't apply to other places
   */
  'priority 1': { group: [LICENSE_APP_GROUP] },
  'priority 2': { group: [LICENSE_APP_GROUP] },
  'priority 3': { group: [LICENSE_APP_GROUP] },
};

export const DATE_TODAY = new Date();

export const INIT_NOTE_ADD_STATUS = { inFlight: false, addedNoteId: null };

export const forgotPasswordPhases = {
  EMAIL_ENTRY: 'EMAIL_ENTRY',
  PASSWORD_SETUP: 'PASSWORD_SETUP',
};

export const NOT_FOUND_ERROR_404 = 'NotFoundError';

export const LOADING_SCREEN_LOADING_TEXT = 'Preparing workspace..';
export const LOADING_ACTIVITIES = 'Loading Activities...';
export const LOADING_PROPERTY_COMPLAINTS = 'Loading Property Complaints...';
export const LOADING_LICENSES = 'Loading Licenses...';
export const LOADING_PROPERTY_HISTORY = 'Loading Property History...';
export const LOADING_PROPERTY_DETAILS = 'Loading Property Details...';
export const LOADING_SCREEN_REFETCH_TEXT = 'Refetching workspace data...';

export const LOGIN_MENU_TYPES = {
  // Used in most if not all login form variations
  SSO_SIGN_IN: 'SSO_SIGN_IN',
  SIGN_IN: 'SIGN_IN',
  FORGOT_PASSWORD_EMAIL: 'FORGOT_PASSWORD_EMAIL',
  FORGOT_PASSWORD_PASSWORD: 'FORGOT_PASSWORD_PASSWORD',
};

export const COMPLAINTS_ENABLED = 'enabled';

export const COMPLAINTS_TYPES = {
  trash: 'Trash',
  noise: 'Noise',
  parking: 'Parking',
  other: 'Other',
  illegal_rental: 'Illegal Rental',
  number_guests: 'Number of Guests',
};

export const COMPLAINTS_STATUS = {
  OPEN: 'open',
  CLOSED: 'closed',
};

export function complaintTypeLabelFactory(complaintType) {
  return COMPLAINTS_TYPES[complaintType] || complaintType;
}

export const DATA_STATUS = {
  inProgress: 'IN_PROGRESS',
  dataError: 'DATA_ERROR',
};

/**
 * Possible values of `type` for activities
 * in the activity feed
 */
export const ACTIVITY_TYPES = {
  COMPLAINT_UPDATE: 'complaint_update',
  NEW_COMPLAINT: 'new_complaint',
  NOTE: 'note',
  GENERATED_FIRST_OUTREACH_LETTER: 'generated_first_outreach_letter',
  GENERATED_SECOND_OUTREACH_LETTER: 'generated_second_outreach_letter',
  GENERATED_THIRD_OUTREACH_LETTER: 'generated_third_outreach_letter',
};

export const ACTIVITY_FEED_PARAMS = {
  INCLUDE_COMPLAINTS: 'include_complaints',
};

/**
 * May be used by data reducers to indicate which fields
 * of a data row are being updated via an API call.
 * The value corresponding to this key should be an array
 * of zero or more field names, which the UI should display as "updating"
 * to provide feedback to the user when editing a single field.
 * One example of such feedback is displaying a loading spinner
 * icon beside an editable field in a table when the user enters
 * a new value and the application is waiting for the update API call
 * to come back.
 */
export const IN_FLIGHT_FIELDS_KEY = '__meta_inFlightFields';

// The dash in place IDs like `ca-san_mateo`
export const PLACE_ID_COMPONENT_SEPARATOR = '-';

export const STATE_CODES = {
  CALIFORNIA: 'CA',
};

export const API_SENDER_MODE_OPTIONS = {
  SHOW_ALL: 'show_all', // also known as the "Include Cities" flag
};

export const HTTP_STATUSES = {
  NOT_FOUND: 404,
  FORBIDDEN: 403,
};

/**
 * This can be used to identify the direction a value has change.
 */
export const VALUE_DIRECTION = {
  NONE: 0,
  INCREASE: 1,
  DECREASE: -1,
};

export const STRORACLE_ENDPOINTS = {
  DEFAULT_PLACE: '/defaultPlace',
  VERSION: '/version',
};

export const PLATFORM_ID_TO_FRIENDLY_NAME = {
  tripadvisor: 'TripAdvisor',
  flipkey: 'Flipkey',
  vrbo: 'Vrbo',
  HIPCAMP: 'Hipcamp',
  BOOK: 'Booking.com',
  airbnb: 'Airbnb',
  other: 'Other',
  none: 'None',
};

export const BETA_FEATURES = {
  licenseEditButtonExpirationDate: 'licenseEditButtonExpirationDate',
  licenseEditButtonAllowedTypes: 'licenseEditButtonAllowedTypes',
  licenseEditButtonRegistrantName: 'licenseEditButtonRegistrantName',
  licenseEditButtonRegistrantFullName: 'licenseEditButtonRegistrantFullName',
  licenseEditButtonOwner: 'licenseEditButtonOwner',

  adminApplyFlow: 'adminApplyFlow',
  adminUpdateFlow: 'adminUpdateFlow',
  adminUpdateFlowRestriction: 'adminUpdateFlowRestriction',

  disableMapClustering: 'disableMapClustering',
  // Property Overview view -> Aggregate Statistics section -> TOT graph
  aggregateStatisticsTOTGraphActual: 'aggregateStatisticsTOTGraphActual',

  acceptChecks: 'acceptChecks',

  supportPage: 'supportPage',

  taxTab: 'taxTab',
  propertyOverviewTaxTab: 'propertyOverviewTaxTab',
  violationsTab: 'violationsTab',
};

export const IS_EDITING_FULL_NAME_FLAG = 'isEditingFullName';
